import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Fade,
  TextField,
  Box,
  MenuItem,
} from '@mui/material';
import LoadingView from './LoadingView';

export interface MaintenanceColumn {
  field: string;
  headerName: string;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  renderCell?: (row: any) => React.ReactNode;
  valueFormatter?: (value: any) => string;
  editable?: boolean;
  type?: 'text' | 'date' | 'select';
  options?: string[];
}

interface MaintenanceDataTableProps {
  rows: any[];
  columns: MaintenanceColumn[];
  loading?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newSize: number) => void;
  onCellUpdate?: (row: any, field: string, value: any) => void;
}

const MaintenanceDataTable: React.FC<MaintenanceDataTableProps> = ({
  rows,
  columns,
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onCellUpdate,
}) => {
  const [editCell, setEditCell] = React.useState<{ rowIndex: number; field: string } | null>(null);

  const handleCellClick = (rowIndex: number, field: string) => {
    setEditCell({ rowIndex, field });
  };

  const handleBlur = () => {
    setEditCell(null);
  };

  const handleValueChange = (row: any, field: string, value: any) => {
    if (onCellUpdate) {
      onCellUpdate(row, field, value);
    }
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TableContainer sx={{ flexGrow: 1, position: 'relative', overflowX: 'auto' }}>
        {loading && <LoadingView message="読み込み中..." height="80vh" size={50} />}
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.align}
                  sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: col.width }}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                {columns.map((col) => {
                  const value = row[col.field];
                  const isEditing = editCell?.rowIndex === i && editCell?.field === col.field;

                  return (
                    <TableCell
                      key={col.field}
                      align={col.align}
                      onClick={() => col.editable && handleCellClick(i, col.field)}
                      sx={{
                        cursor: col.editable ? 'pointer' : 'default',
                        position: 'relative',
                        minWidth: col.width,
                      }}
                    >
                      {isEditing ? (
                        <Fade in={isEditing}>
                          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', px: 1, backgroundColor: 'white', zIndex: 1 }}>
                            <TextField
                              variant="standard"
                              type={col.type === 'date' ? 'date' : 'text'}
                              select={col.type === 'select'}
                              fullWidth
                              autoFocus
                              value={value ?? ''}
                              onBlur={handleBlur}
                              onChange={(e) => handleValueChange(row, col.field, e.target.value)}
                              InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem' } }}
                              SelectProps={{ native: false }}
                            >
                              {col.type === 'select' && col.options?.map((opt) => (
                                <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>
                        </Fade>
                      ) : (
                        <Box sx={{ minHeight: '1.2em' }}>
                          {col.renderCell ? col.renderCell(row) : (col.valueFormatter ? col.valueFormatter(value) : value)}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                  データがありません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onPageSizeChange(parseInt(event.target.value, 10))}
        labelRowsPerPage="表示件数 : "
        sx={{
          minHeight: '40px',
          '& .MuiTablePagination-toolbar': {
            minHeight: '40px !important',
            height: '40px',
          },
        }}
      />
    </Paper>
  );
};

export default MaintenanceDataTable;
